import React, { useRef, useLayoutEffect } from "react";
import PropTypes from "prop-types";

import { useViewport } from "js/hooks/useViewport";

import { productMenu, homePath, photosLandingPath } from "data/links";

import NpHeaderLogo from "./images/NpHeaderLogo";

import { OverflowMenu } from "components/OverflowMenu";
import { ProfileMenu } from "components/ProfileMenu";
import DefaultSearch from "./DefaultSearch";

import Flexbox from "core/Flexbox";
import { Col } from "core/grid";
import colors from "core/colors";

import {
    animateScrollingDown,
    animateScrollingBackUp,
    scrollPosAnimation,
    scrollPosShowHideSearch,
    scrollSearchBarScrollingDown,
    scrollSearchBarScrollingUp
} from "./helpers";

import {
    NonMobileContentWrapper,
    TextLinkGrid,
    NonMobileTopRow,
    MainLinksCol,
    ProfileMenuCol,
    SearchContainerRow,
    MainLinks,
    Link,
    HeaderLogoLink,
    SearchWrapper
} from "./styles";

const NonMobileContent = ({
    transparent,
    currentUser,
    loginLink,
    joinLink,
    handleLogOut,
    heroSearchBgColor,
    isHeroSearchV2,
    siteHeaderRef
}) => {
    const navRef = useRef(null);
    const navLinkRef = useRef(null);
    const logoRef = useRef(null);
    const profileMenuRef = useRef(null);
    const searchContainerRef = useRef(null);
    const searchWrapperRef = useRef(null);

    // needed to adjust the scroll position for header animations for each breakpoint:
    const { browserWidth } = useViewport();

    // animate header independently of header search based on scroll position:
    const scrollPositionAnimation = scrollPosAnimation({
        browserWidth,
        isHeroSearchV2
    });

    const handleScroll = () => {
        if (window.pageYOffset > scrollPositionAnimation) {
            animateScrollingDown({
                navRef,
                searchContainerRef,
                navLinkRef,
                profileMenuRef,
                heroSearchBgColor,
                siteHeaderRef
            });
        } else if (window.pageYOffset < scrollPositionAnimation) {
            animateScrollingBackUp({
                navRef,
                searchContainerRef,
                navLinkRef,
                profileMenuRef,
                heroSearchBgColor,
                siteHeaderRef
            });
        }
    };

    // show and hide hero search and header search based on scroll position:
    const scrollPositionShowHideHeroSearch = scrollPosShowHideSearch({
        browserWidth,
        isHeroSearchV2
    });

    const handleScrollSearchBar = () => {
        if (
            searchWrapperRef.current &&
            searchWrapperRef.current.style.display !== "block" &&
            (location.pathname === homePath.url ||
                location.pathname === photosLandingPath)
            // ^^ add location.pathname === path-to-page when isHeroSearchV2
        ) {
            searchWrapperRef.current.setAttribute("style", "display:block");
        }

        const heroSearchWrapper = document.getElementById(
            "hero-search-wrapper"
        );

        if (window.pageYOffset > scrollPositionShowHideHeroSearch) {
            scrollSearchBarScrollingDown({
                heroSearchWrapper,
                isHeroSearchV2,
                searchWrapperRef
            });
        } else if (window.pageYOffset < scrollPositionShowHideHeroSearch) {
            scrollSearchBarScrollingUp({
                heroSearchWrapper,
                isHeroSearchV2,
                searchWrapperRef
            });
        }
    };

    useLayoutEffect(() => {
        if (
            searchWrapperRef.current &&
            searchWrapperRef.current.style.display !== "none" &&
            (location.pathname === homePath.url ||
                location.pathname === photosLandingPath)
            // ^^ add location.pathname === path-to-page when isHeroSearchV2
        ) {
            searchWrapperRef.current.setAttribute("style", "display:none");
        }

        window.addEventListener("scroll", handleScroll);
        window.addEventListener("scroll", handleScrollSearchBar);

        return () => {
            window.removeEventListener("scroll", () => handleScroll);
            window.removeEventListener("scroll", () => handleScrollSearchBar);
        };
    }, []);

    return (
        <NonMobileContentWrapper>
            <TextLinkGrid>
                <NonMobileTopRow between="md" middle="md">
                    <MainLinksCol lg={5} md={7} ref={navRef}>
                        <MainLinks
                            alignItems="center"
                            transparent={transparent}
                            ref={navLinkRef}
                        >
                            {productMenu.map(link => (
                                <Link
                                    key={link.name}
                                    href={link.href}
                                    target={link.external ? "_blank" : "_self"}
                                >
                                    {link.name}
                                </Link>
                            ))}

                            <OverflowMenu
                                transparent={transparent}
                                isHeroSearchV2={isHeroSearchV2}
                            />
                        </MainLinks>
                    </MainLinksCol>
                    <ProfileMenuCol
                        mdOffset={3}
                        md={2}
                        lgOffset={6}
                        lg={1}
                        ref={profileMenuRef}
                    >
                        <Flexbox justifyContent="flex-end">
                            <ProfileMenu
                                isHeroSearchV2={isHeroSearchV2}
                                currentUser={currentUser}
                                loginLink={loginLink}
                                joinLink={joinLink}
                                handleLogOut={handleLogOut}
                            />
                        </Flexbox>
                    </ProfileMenuCol>
                </NonMobileTopRow>
                <SearchContainerRow
                    middle="md"
                    ref={searchContainerRef}
                    isHeroSearchV2={isHeroSearchV2}
                >
                    <Col md={2}>
                        <HeaderLogoLink href={homePath.url} ref={logoRef}>
                            <NpHeaderLogo
                                title={homePath.alt}
                                fillColor={
                                    isHeroSearchV2 ? colors.snow : colors.black
                                }
                            />
                        </HeaderLogoLink>
                    </Col>
                    <Col md={8}>
                        <SearchWrapper ref={searchWrapperRef}>
                            <DefaultSearch />
                        </SearchWrapper>
                    </Col>
                </SearchContainerRow>
            </TextLinkGrid>
        </NonMobileContentWrapper>
    );
};

NonMobileContent.propTypes = {
    isHeroSearchV2: PropTypes.bool,
    heroSearchBgColor: PropTypes.string,
    transparent: PropTypes.bool,
    currentUser: PropTypes.object,
    loginLink: PropTypes.oneOfType([PropTypes.node, PropTypes.element])
        .isRequired,
    joinLink: PropTypes.oneOfType([PropTypes.node, PropTypes.element])
        .isRequired,
    handleLogOut: PropTypes.func.isRequired
};

export { NonMobileContent };
